import type { ReactNode } from "react";
import { Spinner } from "@/components/ui";
import { cn } from "@/lib/cn";
import { OBJECT_OPEN_EM } from "@/lib/frame-card-shape";
import type { CardPaint } from "./card-paint";
import { useFrameSurface } from "./use-frame-surface";
import { useOpenedFrame } from "./use-opened-frame";

/**
 * The type of an object's composition: its frame's width over the card's
 * width in em. An opened window is `OBJECT_OPEN_EM` × `OBJECT_OPEN_FS` wide,
 * so it reads at `OBJECT_OPEN_FS`; a canvas node or an embed reads at its own
 * width.
 */
const OBJECT_TYPE = `calc(100cqw / ${OBJECT_OPEN_EM})`;

/**
 * A card part that fills an object frame: it takes the frame's height, or
 * its content's when that is taller (the frame then scrolls). Overrides the
 * card's fixed height.
 */
export const OPENED_PART = "h-auto min-h-fit flex-1";

/**
 * An object frame (weather, flight, music, chart, event, link, file; Part 10
 * Rule 1): its card's em composition, larger, then what the card had no room
 * for. The parts grow into the frame (`OPENED_PART`), so the composition
 * spreads over the opened window it was sized for, and scrolls in a smaller
 * box.
 *
 * A gadget's paint: an opened window already wears it (`--frame-paint`, the
 * same `cardPaint`, set by its shell), so the frame paints nothing there and
 * never a second background. Off an opened window (a canvas node, a Pinned
 * tile, an inline embed) the frame paints it itself.
 */
export function ObjectFrame({
  id,
  paint,
  testId,
  className,
  children,
}: {
  id: string;
  paint: CardPaint;
  testId: string;
  /** The body's layout (gap, padding) in em. */
  className?: string;
  children: ReactNode;
}) {
  const frameSurface = useFrameSurface(id);
  const opened = useOpenedFrame();
  return (
    <div
      className={cn(
        frameSurface.className,
        // Its own width sets the type, so it never sizes to its content.
        "relative flex h-full w-full flex-col overflow-hidden [container-type:inline-size]",
      )}
      style={
        opened || paint.ink === "card"
          ? undefined
          : { background: paint.background }
      }
      data-testid={testId}
      {...frameSurface.attributes}
    >
      <div
        className={cn(
          "flex h-full flex-col overflow-y-auto leading-[1.45] text-card-ink",
          className,
        )}
        style={{ fontSize: OBJECT_TYPE }}
        data-frame-measure-content="true"
        data-frame-drag-handle="true"
      >
        {children}
      </div>
    </div>
  );
}

/** What an empty object frame says while its agent is still filling it. */
export function ObjectFrameLoading({
  testId,
  children,
}: {
  testId: string;
  children: ReactNode;
}) {
  return (
    <p
      className="flex flex-1 items-center justify-center gap-[0.5em] text-[0.6em] text-card-muted"
      data-testid={testId}
    >
      <Spinner className="h-[1em] w-[1em] shrink-0" />
      {children}
    </p>
  );
}
